import { useCallback, useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { SupportedMediaType } from "@hl/shared-features/lib/apollo/graphql.generated";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import { Anchor, Button, Group, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import {
  fileTypeFromExtension,
  typeFromMime,
} from "~/features/../utils/media/file";
import MintPreviewMediaUploader, {
  MediaFile,
} from "~/features/collections/forms/CreateCollectionMultistepForm/MintPreviewMediaUploader";
import {
  GetManageCollectionDocument,
  GetManageCollectionQuery,
} from "~/features/manage-collection/v2.queries.graphql.generated";
import { PropertyBox } from "~/features/tokens/sections/ManageToken/properties/PropertyBox";
import { useUpdateCollectionOffChainMutation } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

type Props = {
  collection: GetManageCollectionQuery["getManageCollection"];
};
export const SeriesPreviewImages = ({ collection }: Props) => {
  const images = collection.seriesDetails?.mintPreviewImageUrls ?? [];
  return (
    <>
      <PropertyBox
        label="Preview images"
        value={
          <Group position="apart" w="100%" align="start" noWrap>
            <Group>
              {images.map((url) => (
                <div key={url}>
                  {isVideoUrl(url) ? (
                    <video controls loop playsInline height="64px">
                      <source src={url} />
                    </video>
                  ) : (
                    <ResizedImage width={64} src={url} />
                  )}
                </div>
              ))}
            </Group>
            <EditPreviewImagesBtn
              initImages={images}
              collectionId={collection.id}
            />
          </Group>
        }
      />
    </>
  );
};

const EditPreviewImagesBtn = ({
  initImages,
  collectionId,
}: {
  initImages: string[];
  collectionId: string;
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  const [updateCollectionOffChain, { loading, error }] =
    useUpdateCollectionOffChainMutation({
      onCompleted: close,
    });
  const saveChanges = useCallback(
    (urls: string[]) => {
      updateCollectionOffChain({
        variables: {
          collectionId,
          data: {
            seriesDetails: {
              mintPreviewImageUrls: urls,
            },
          },
        },
        refetchQueries: [GetManageCollectionDocument],
      });
    },
    [collectionId]
  );

  return (
    <>
      <Anchor w="auto" underline={false} onClick={open}>
        <Text size="sm" fw={WEIGHT_BOLD}>
          Edit
        </Text>
      </Anchor>
      {opened && (
        <EditImagesModal
          opened={opened}
          initImages={initImages}
          close={close}
          onSave={saveChanges}
          saving={loading}
          saveError={!!error}
        />
      )}
    </>
  );
};

const isVideoUrl = (url: string) => {
  const fileType = fileTypeFromExtension(url);
  const mediType = typeFromMime[fileType];
  return mediType === SupportedMediaType.VIDEO;
};

const EditImagesModal = ({
  opened,
  close,
  initImages,
  onSave,
  saving,
  saveError,
}: {
  opened: boolean;
  saving: boolean;
  saveError: boolean;
  close: () => void;
  initImages: string[];
  onSave: (urls: string[]) => void;
}) => {
  const [images, setImages] = useState<MediaFile[]>(
    initImages.map((url, index) => ({
      url,
      type: fileTypeFromExtension(url),
      name: `${(index + 1).toString()}.${url.split(".").pop()}`,
    }))
  );
  const [isChanged, setChanged] = useState(false);
  const updateImages = useCallback((images: MediaFile[]) => {
    setImages(images);
    setChanged(true);
  }, []);
  return (
    <Modal
      size="lg"
      opened={opened}
      onClose={close}
      title="Edit preview images"
      styles={{
        modal: {
          // causes problems with dnd
          // https://github.com/hello-pangea/dnd/blob/main/docs/api/draggable.md#warning-position-fixed
          transform: "none !important",
        },
      }}
    >
      <MintPreviewMediaUploader
        mediaFiles={images}
        onChange={updateImages}
        supportedTypes={[SupportedMediaType.IMAGE, SupportedMediaType.VIDEO]}
        mediaError={""}
      />
      <ModalSection>
        <Button
          size="xl"
          fullWidth
          loading={saving}
          disabled={!isChanged}
          onClick={() => onSave(images.map((x) => x.url))}
        >
          Save changes
        </Button>
        {saveError && (
          <Text size="sm" color={STATUS_COLOR.ERROR} pt="sm">
            Error while saving
          </Text>
        )}
      </ModalSection>
    </Modal>
  );
};
